// src/oura/components/newscan/MarketList.tsx
//
// The Business step's Markets for a business found by what it sells (Online):
// where it sells, in priority order, the home market LAST (it gets the fewest
// questions). One row per market: its place, its name, a "Home" tag on the
// last row, and Move up / Move down / Remove. The home row has no move or
// remove buttons, and Move down on the row above it is disabled, so the home
// market stays last. Disabled, not hidden, so the focus order holds.
//
// Under the list: an add row (a native select of the markets not listed, and
// Add), then the Home market select. Picking a home market moves it to the
// last row, adding it when it is not listed.
//
// After a move, focus stays on the same button of the moved row and a polite
// line says "Singapore is now 1 of 3."; after a remove, focus goes to the next
// row's Remove, else the add select. "Any market" is never pickable here.

import { ArrowDownIcon, ArrowUpIcon, XIcon } from "lucide-react";
import { useId, useRef, useState } from "react";
import type { ScanDraftMarket } from "@service/aeo/scanDraftTypes";
import { cn } from "@oura/lib/cn";
import { FOCUS_RING, PillButton, Tag, TYPE } from "@oura/ui";
import { inputClass } from "@oura/onboarding/messageText";
import { Muted } from "@oura/components/newscan/Muted";
import { MARKETS_MAX, PICKABLE_MARKETS, marketOf } from "@oura/newscan/markets";

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const COPY = {
    home: "Home",
    up: (m: string) => `Move ${m} up`,
    down: (m: string) => `Move ${m} down`,
    remove: (m: string) => `Remove ${m}`,
    position: (i: number) => `${i}.`,
    addLabel: "Market to add",
    add: "Add",
    homeLabel: "Home market",
    homePick: "Pick the home market",
    hint: "In priority order. The first gets the most questions. The home market goes last with the fewest.",
    empty: "No markets yet.",
    need: "Add at least 1 market",
    full: `Up to ${MARKETS_MAX} markets.`,
    nowAt: (m: string, at: number, of: number) => `${capital(m)} is now ${at} of ${of}.`,
    added: (m: string) => `Added ${m}.`,
    removed: (m: string) => `Removed ${m}.`,
    homeNow: (m: string) => `${capital(m)} is the home market.`,
} as const;

const ICON_BUTTON = cn(
    // 28px visible, 44px to the touch through the pseudo-element.
    "relative grid h-7 w-7 shrink-0 place-items-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700",
    "after:absolute after:-inset-2 after:content-[''] motion-safe:transition-colors",
    "disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent",
    FOCUS_RING,
);

export interface MarketListProps {
    /** Priority order, home last. */
    markets: ScanDraftMarket[];
    onChange(next: ScanDraftMarket[]): void;
    disabled?: boolean;
    /** id of the visible label (the DefRow's dt), for the list's name. */
    labelledBy?: string;
    /** Show "Add at least 1 market" when the list is empty. */
    showNeed?: boolean;
}

export function MarketList({ markets, onChange, disabled, labelledBy, showNeed = true }: MarketListProps) {
    const base = useId();
    const addId = `${base}-add`;
    const homeId = `${base}-home`;
    const hintId = `${base}-hint`;
    const needId = `${base}-need`;
    const listRef = useRef<HTMLOListElement>(null);
    const addRef = useRef<HTMLSelectElement>(null);
    const [said, setSaid] = useState("");
    const say = (line: string) => setSaid((was) => (was === line ? `${line}\u200b` : line));

    const listed = new Set(markets.map((m) => m.code));
    const notListed = PICKABLE_MARKETS.filter((m) => !listed.has(m.code));
    const [toAdd, setToAdd] = useState<string>("");
    const addValue = notListed.some((m) => m.code === toAdd) ? toAdd : notListed[0]?.code ?? "";
    const full = markets.length >= MARKETS_MAX;
    const home = markets.length ? markets[markets.length - 1] : null;
    const empty = markets.length === 0;

    const focusIn = (code: string, which: "up" | "down" | "remove") =>
        requestAnimationFrame(() => {
            const row = listRef.current?.querySelector<HTMLElement>(`[data-market-row="${code}"]`);
            const order: Array<"up" | "down" | "remove"> = which === "up" ? ["up", "down", "remove"] : which === "down" ? ["down", "up", "remove"] : ["remove"];
            for (const w of order) {
                const b = row?.querySelector<HTMLButtonElement>(`[data-market-${w}]`);
                if (b && !b.disabled) {
                    b.focus();
                    return;
                }
            }
            addRef.current?.focus();
        });

    const move = (i: number, by: -1 | 1) => {
        const j = i + by;
        // The home row (last) never moves, and nothing moves below it.
        if (i < 0 || j < 0 || i >= markets.length - 1 || j >= markets.length - 1) return;
        const next = [...markets];
        [next[i], next[j]] = [next[j], next[i]];
        onChange(next);
        say(COPY.nowAt(next[j].label, j + 1, next.length));
        focusIn(next[j].code, by === -1 ? "up" : "down");
    };

    const remove = (i: number) => {
        const gone = markets[i];
        if (!gone || i === markets.length - 1) return;
        const next = markets.filter((_, k) => k !== i);
        onChange(next);
        say(COPY.removed(gone.label));
        // The next row's Remove (the home row has none), else the add select.
        const after = next[i];
        if (after && i < next.length - 1) focusIn(after.code, "remove");
        else requestAnimationFrame(() => addRef.current?.focus());
    };

    const add = () => {
        const m = marketOf(addValue);
        if (!m || listed.has(m.code) || full) return;
        // Above the home row; the first market added is the home market.
        const next = markets.length ? [...markets.slice(0, -1), m, markets[markets.length - 1]] : [m];
        onChange(next);
        say(COPY.added(m.label));
        setToAdd("");
        // Focus stays on the add row while it is there; once full, the new row's Remove.
        requestAnimationFrame(() => {
            if (addRef.current) addRef.current.focus();
            else focusIn(m.code, "remove");
        });
    };

    const setHome = (code: string) => {
        const m = marketOf(code);
        if (!m || home?.code === m.code) return;
        let rest = markets.filter((x) => x.code !== m.code);
        // Full and the new home not listed: it takes the old home's place.
        if (rest.length >= MARKETS_MAX) rest = rest.slice(0, MARKETS_MAX - 1);
        onChange([...rest, m]);
        say(COPY.homeNow(m.label));
    };

    return (
        <div className="min-w-0 space-y-2">
            {empty ? (
                <Muted as="p" className="py-1.5">
                    {COPY.empty}
                </Muted>
            ) : (
                <ol ref={listRef} aria-labelledby={labelledBy} className="list-plain m-0 space-y-1 p-0">
                    {markets.map((m, i) => {
                        const isHome = i === markets.length - 1;
                        return (
                            <li key={m.code} data-market-row={m.code} className="flex min-h-10 min-w-0 items-center gap-2">
                                <span aria-hidden className={cn(TYPE.tc, "w-5 shrink-0 text-right tabular-nums text-gray-400")}>
                                    {COPY.position(i + 1)}
                                </span>
                                <span className={cn(TYPE.tb, "min-w-0 truncate font-medium text-gray-900")}>
                                    <span className="sr-only">{`${i + 1} of ${markets.length}: `}</span>
                                    {capital(m.label)}
                                </span>
                                {isHome && <Tag tone="gray">{COPY.home}</Tag>}
                                {!isHome && (
                                    <span className="ml-auto flex shrink-0 items-center gap-2">
                                        <button
                                            type="button"
                                            data-market-up=""
                                            disabled={disabled || i === 0}
                                            aria-label={COPY.up(m.label)}
                                            title={COPY.up(m.label)}
                                            onClick={() => move(i, -1)}
                                            className={ICON_BUTTON}
                                        >
                                            <ArrowUpIcon aria-hidden size={14} />
                                        </button>
                                        <button
                                            type="button"
                                            data-market-down=""
                                            disabled={disabled || i >= markets.length - 2}
                                            aria-label={COPY.down(m.label)}
                                            title={COPY.down(m.label)}
                                            onClick={() => move(i, 1)}
                                            className={ICON_BUTTON}
                                        >
                                            <ArrowDownIcon aria-hidden size={14} />
                                        </button>
                                        <button
                                            type="button"
                                            data-market-remove=""
                                            disabled={disabled}
                                            aria-label={COPY.remove(m.label)}
                                            title={COPY.remove(m.label)}
                                            onClick={() => remove(i)}
                                            className={ICON_BUTTON}
                                        >
                                            <XIcon aria-hidden size={14} />
                                        </button>
                                    </span>
                                )}
                            </li>
                        );
                    })}
                </ol>
            )}

            {notListed.length > 0 && !full && (
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <select
                        ref={addRef}
                        id={addId}
                        aria-label={COPY.addLabel}
                        aria-describedby={empty && showNeed ? needId : undefined}
                        aria-invalid={empty && showNeed ? true : undefined}
                        value={addValue}
                        disabled={disabled}
                        onChange={(e) => setToAdd(e.target.value)}
                        className={cn(inputClass(empty && showNeed ? COPY.need : null), "min-w-0 flex-1 sm:w-56 sm:flex-none")}
                    >
                        {notListed.map((m) => (
                            <option key={m.code} value={m.code}>
                                {capital(m.label)}
                            </option>
                        ))}
                    </select>
                    <PillButton size="sm" disabled={disabled} onClick={add} className="max-sm:h-9">
                        {COPY.add}
                    </PillButton>
                </div>
            )}
            {full && notListed.length > 0 && (
                <Muted as="p" tone="ink4">
                    {COPY.full}
                </Muted>
            )}
            {empty && showNeed && (
                <p id={needId} className={cn(TYPE.tc, "m-0 font-medium text-rose-600")}>
                    {COPY.need}
                </p>
            )}

            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 pt-1">
                <label htmlFor={homeId} className={cn(TYPE.lab)}>
                    {COPY.homeLabel}
                </label>
                <select
                    id={homeId}
                    value={home?.code ?? ""}
                    disabled={disabled}
                    aria-describedby={hintId}
                    onChange={(e) => setHome(e.target.value)}
                    className={cn(inputClass(), "min-w-0 flex-1 sm:w-56 sm:flex-none")}
                >
                    {!home && (
                        <option value="" disabled>
                            {COPY.homePick}
                        </option>
                    )}
                    {/* The listed markets first (in their order), then the rest. */}
                    {[...markets, ...notListed].map((m) => (
                        <option key={m.code} value={m.code}>
                            {capital(m.label)}
                        </option>
                    ))}
                </select>
            </div>
            <Muted as="p" id={hintId}>
                {COPY.hint}
            </Muted>

            <span aria-live="polite" className="sr-only">
                {said}
            </span>
        </div>
    );
}
